import { campaigns } from '@bb/contracts';

export type ScheduledDelivery = campaigns.ScheduledDelivery;
export type ScheduleKind = ScheduledDelivery['kind'];
export type RecurringSchedule = Extract<ScheduledDelivery, { kind: 'recurring' }>;
export type Frequency = RecurringSchedule['every'];

/** ISO weekdays (1 = Monday), in form order. */
export const ISO_WEEKDAYS = [
  { day: 1, label: 'Monday' },
  { day: 2, label: 'Tuesday' },
  { day: 3, label: 'Wednesday' },
  { day: 4, label: 'Thursday' },
  { day: 5, label: 'Friday' },
  { day: 6, label: 'Saturday' },
  { day: 7, label: 'Sunday' },
] as const;

export const DEFAULT_SEND_TIME = '10:00';

const pad = (n: number) => String(n).padStart(2, '0');

/** Tomorrow in the browser's calendar: the default date of a new one-off or recurring schedule. */
export function tomorrow(now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const utcDate = (date: string) => {
  const d = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** ISO weekday of a `YYYY-MM-DD` date, Monday when the date is not valid. */
export const isoWeekdayOf = (date: string) => {
  const d = utcDate(date);
  return d ? d.getUTCDay() || 7 : 1;
};

const dayOfMonthOf = (date: string) => utcDate(date)?.getUTCDate() ?? 1;

const sharedFields = (value: ScheduledDelivery) =>
  value.kind === 'at'
    ? { date: value.date, time: value.time, localTime: value.localTime }
    : value.kind === 'recurring'
      ? { date: value.startDate, time: value.time, localTime: value.localTime }
      : { date: tomorrow(), time: DEFAULT_SEND_TIME, localTime: false };

/** The value for another mode: date, time and local time carry over, everything else starts from valid defaults. */
export function switchScheduleKind(value: ScheduledDelivery, kind: ScheduleKind): ScheduledDelivery {
  if (value.kind === kind) return value;
  if (kind === 'immediate') return { kind: 'immediate' };
  const { date, time, localTime } = sharedFields(value);
  if (kind === 'at') return { kind: 'at', date, time, localTime };
  return { kind: 'recurring', every: 'day', interval: 1, time, localTime, startDate: date };
}

/** Changes the frequency: weekly starts on the start date's weekday, monthly on its day of the month. */
export function withFrequency(value: RecurringSchedule, every: Frequency): RecurringSchedule {
  const { daysOfWeek, dayOfMonth, ...rest } = value;
  if (every === 'week') return { ...rest, every, daysOfWeek: daysOfWeek?.length ? daysOfWeek : [isoWeekdayOf(rest.startDate)] };
  if (every === 'month') return { ...rest, every, dayOfMonth: dayOfMonth ?? dayOfMonthOf(rest.startDate) };
  return { ...rest, every };
}

/** Turns a weekday on or off; no days left drops the field, which the contract rejects for weekly schedules. */
export function toggleWeekday(value: RecurringSchedule, day: number, on: boolean): RecurringSchedule {
  const days = new Set(value.daysOfWeek ?? []);
  if (on) days.add(day);
  else days.delete(day);
  const { daysOfWeek: _old, ...rest } = value;
  return days.size ? { ...rest, daysOfWeek: [...days].sort((a, b) => a - b) } : rest;
}

const MESSAGES: Record<string, string> = {
  date: 'Enter a date.',
  time: 'Enter a time.',
  startDate: 'Enter a start date.',
  endDate: 'Enter a valid end date.',
  daysOfWeek: 'Choose at least one day of the week.',
  dayOfMonth: 'Choose a day of the month.',
  interval: 'Choose an interval from 1 to 12.',
};

/**
 * Client-side check of the contract (`campaigns.ScheduledDelivery`), one message per field. The server adds what
 * needs the environment's time zone (the 24-hour rule of one-off local-time sends).
 */
export function scheduleIssues(value: ScheduledDelivery): Record<string, string> {
  const parsed = campaigns.ScheduledDelivery.safeParse(value);
  if (parsed.success) return {};
  const issues: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    const field = String(issue.path[0] ?? 'schedule');
    const message = field === 'endDate' && issue.message === 'endDate is before startDate' ? 'The end date is before the start date.' : (MESSAGES[field] ?? issue.message);
    issues[field] ??= message;
  }
  return issues;
}

/** A send instant in a time zone, e.g. `Tue 2031-05-20 09:30`; an unknown zone falls back to UTC. */
export function formatSendTime(iso: string, timeZone: string): string {
  const format = (zone: string) =>
    new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(iso));
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = format(timeZone);
  } catch {
    parts = format('UTC');
  }
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('weekday')} ${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}`;
}
